
import { Component, Vue, Emit, Watch, Prop } from "vue-property-decorator";
import rest from "@/rest";
import { l3draggable, l3droppable } from "@/directives/l3dragDrop";
// components
import TusFileUploadOverlay from '../Shared/TusFileUploadOverlay.vue';
// interfaces
import { namespace } from "vuex-class";
import { ExerciseVM } from "@/interfaces/ExerciseVM";
import { CourseElementVm } from "@/interfaces/CourseElementVm";
import { FileDetailsVm } from "@/interfaces/FileDetailsVm";
import { ParamDictionary } from "@/interfaces/ParamDictionary";
import { CourseElementType } from "@/interfaces/CourseElementType";
import MarkdownEditor from "../Shared/MarkdownEditor.vue";
import CompAudioPlayerWithSubtitles from "../ExerciseComponents/CompAudioPlayerWithSubtitles.vue";

const globals = namespace("globals");

@Component({
  components: {
    CompAudioPlayerWithSubtitles,
    TusFileUploadOverlay,
    MarkdownEditor
  },
  directives: { l3draggable, l3droppable }
})
export default class AudioElement extends Vue {
  @Prop() element!: CourseElementVm;

  @globals.Getter selectedExercise!: ExerciseVM;
  // @globals.Action refreshSelectedElement!: () => Promise<void>;

  isBusy = false;
  showMarkdownEditor = false;
  fileToUpload: File | null = null;

  mounted() {}

  openMarkdownEditor() {
    this.showMarkdownEditor = true;
  }

  // async onNewFileDropped(fileDetails: FileDetailsVm) {
  //   if (fileDetails.fileType != "Audio" && fileDetails.fileType != "Video") {
  //     this.$store.commit("ux/SB_FAILURE", {
  //       message: "Bei dieser Datei handelt es sich nicht um eine Audio-Datei.",
  //       timeout: 0
  //     });
  //     return;
  //   }
  //   this.element.contentUrl = null;
  //   await this.$globalHelper.delay(5);
  //   this.element.contentUrl = fileDetails.filePath;
  //   this.element.aiSummary = fileDetails.aiSummary;
  // }

  // async onMediaFileUploaded(response: any): Promise<void> {
  //   if (!response.success) {
  //     this.$store.commit("ux/SB_FAILURE", {
  //       message: response.message,
  //       timeout: 0
  //     });
  //     return;
  //   }

  //   let params: ParamDictionary = { dictionary: {}};
  //   params.dictionary!["OldFilePath"] = this.element.contentUrl ?? "";
  //   params.dictionary!["UploadedFileName"] = response.fileName!;
  //   params.dictionary!["BundleId"] = this.selectedExercise.exBundleId!.toString();
  //   await rest.url("contentManager/moveFileToBundleDir").post(params)
  //     .then(async (response) => {
  //       if (!response) {
  //         // vuex.globals.setBundle(null);
  //         // await this.updateBundles();
  //         return;
  //       }

  //       // If video is dropped on audio element, convert to audio
  //       if (this.element.type == CourseElementType[CourseElementType.Audio] && (response as string).endsWith(".mp4")) {
  //         console.log("convert video file to audio...");
  //         params.dictionary!["UploadedFileName"] = response;
  //         this.isBusy = true;
  //         await rest.url("contentManager/extractAudio").post(params)
  //           .then(async (response) => {
  //             this.element.contentUrl = null;
  //             await this.$globalHelper.delay(5);
  //             this.element.contentUrl = response;
  //             this.element.aiSummary = null;
  //           })
  //           .catch(err => {
  //           })
  //           .finally(() => {
  //             this.isBusy = false;
  //           });
  //         return;
  //       }

  //       // Reload the element to get the updated AI summary
  //       // await this.refreshSelectedElement();

  //       // Force update of audio element
  //       this.element.contentUrl = null;
  //       await this.$globalHelper.delay(5);
  //       this.element.contentUrl = response;
  //       this.element.aiSummary = null;
  //     })
  //     .catch(err => {
  //     })
  //     .finally(() => {
  //       this.isBusy = false;
  //     });
  // }

  async onUploaded(filePath: string | null) {
    // console.log("onUploaded response:", filePath);
    if (!filePath) return;
    const fileName = this.$globalHelper.extractFileName(filePath as string);

    // If video is dropped on audio element, convert to audio
    if (this.element.type == CourseElementType[CourseElementType.Audio] && (fileName as string).endsWith(".mp4")) {
      console.log("convert video file to audio...");
      this.isBusy = true;
      let params: ParamDictionary = { dictionary: {} };
      params.dictionary!["OldFilePath"] = this.element.contentUrl ?? "";
      params.dictionary!["UploadedFileName"] = fileName;
      params.dictionary!["BundleId"] = this.selectedExercise.exBundleId!.toString();
      try {
        const resp = await rest.url("contentManager/extractAudio").post(params);
        this.element.contentUrl = null;
        await this.$globalHelper.delay(5);
        this.element.contentUrl = resp;
        this.element.aiSummary = null;
        this.$root.$emit('fileListUpdate:MediaPool'); // notify media pool via global event to refresh file list
      } catch (err) {
        // ignore
      } finally {
        this.isBusy = false;
      }
      return;
    }

    // Normal case: set the element content URL to the moved path
    this.element.contentUrl = null;
    await this.$globalHelper.delay(5);
    this.element.contentUrl = filePath;
    this.element.aiSummary = null;
  }

  onCancelled() {
    // this.fileToUpload = null;
  }

  get dropOptions() {
    return {
      accept: (data: any, ev?: DragEvent) => {
        if (!data) return false;
        // Check for FileDetailsVm
        if (data?.fileType && (data.fileType == "Audio" || data.fileType == "Video")) return true;
        // Check for raw file array (dragged from OS / Browser)
        if (Array.isArray(data) && data[0] && data[0].name && data[0].type) return true;
        return false;
      },
      hoverClass: "drop-hover",
      transferTypes: ["Files"],
      targetGroups: ["audio-file-details", "video-file-details"],
      onDrop: async (data: any) => {
        // FileDetailsVm dropped from app
        if (data?.fileType && (data.fileType == "Audio" || data.fileType == "Video")) {
          let fileDetails: FileDetailsVm = data;
          if (data.fileType == "Video") {
            // Video dropped on audio element: convert to audio
            this.onUploaded(fileDetails.filePath);
            return;
          }
          this.element.contentUrl = null;
          await this.$globalHelper.delay(5);
          this.element.contentUrl = fileDetails.filePath;
          this.element.aiSummary = fileDetails.aiSummary || null;
        } else if (Array.isArray(data) && data[0] && data[0].name && data[0].type) {
          // Raw file dropped from OS/browser
          const f = data[0];
          if (!(f.type.startsWith("audio/") || f.type.startsWith("video/"))) {
            this.$store.commit("ux/SB_FAILURE", {
              message: "Bei dieser Datei handelt es sich nicht um eine Audio-/Videodatei.",
              timeout: 0
            });
            return;
          }
          this.fileToUpload = f; // trigger upload overlay
        }
      }
    };
  }
}
